import { useState } from 'react';
import type { Patient } from '../../../types';
import { formatDisplayDate } from '../../../utils/displayDate';
import './CompactPatientHeader.scss';

interface PatientDetailsGridProps {
  patient: Patient;
}

/**
 * Render the patient information grid used by the document summary header.
 *
 * @param props - Patient data displayed in the grid.
 * @returns The patient information grid.
 */
export function PatientDetailsGrid({ patient }: PatientDetailsGridProps) {
  const [hoveredItem, setHoveredItem] = useState<string | null>(null);

  return (
    <div
      className="compact-patient-header"
      style={{
        borderRadius: '0 0 0.5rem 0.5rem',
        marginBottom: '1.5rem',
        borderTop: 'none',
      }}
    >
      <div className="patient-details-grid">
        {/* Row 1: Allergies | Diagnoses | Care Teams | Upcoming Orders */}
        <div className="detail-row">
          {patient.allergies && patient.allergies.length > 0 ? (
            <div className="detail-item">
              <span className="detail-label">Allergies</span>
              <div
                className="detail-value-wrapper"
                onMouseEnter={patient.allergies.length > 1 ? () => setHoveredItem('allergies') : undefined}
                onMouseLeave={patient.allergies.length > 1 ? () => setHoveredItem(null) : undefined}
              >
                <span className={`detail-value ${patient.allergies.length > 1 ? 'detail-clickable' : ''}`}>
                  {patient.allergies[0]?.substance}
                  {patient.allergies[0]?.type && ` (${patient.allergies[0].type})`}
                  {patient.allergies.length > 1 && ` +${patient.allergies.length - 1}`}
                </span>
                {hoveredItem === 'allergies' && patient.allergies.length > 1 && (
                  <div className="detail-dropdown" style={{ textAlign: 'left' }}>
                    {patient.allergies.map((allergy, index) => (
                      <div key={index} className="dropdown-item">
                        <span>{allergy.substance}</span>
                        {allergy.type && <span className="allergy-type"> ({allergy.type})</span>}
                        {allergy.severity && (
                          <span className="allergy-severity"> · {allergy.severity}</span>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="detail-item">
              <span className="detail-label">Allergies</span>
              <span className="detail-value">No allergies</span>
            </div>
          )}

          {patient.diagnoses && patient.diagnoses.length > 0 ? (
            <div
              className="detail-item detail-hover"
              onMouseEnter={() => setHoveredItem('diagnoses')}
              onMouseLeave={() => setHoveredItem(null)}
            >
              <span className="detail-label">Diagnoses</span>
              <span className="detail-value detail-clickable">
                {patient.diagnoses.length} {patient.diagnoses.length === 1 ? 'diagnosis' : 'diagnoses'}
              </span>
              {hoveredItem === 'diagnoses' && (
                <div className="detail-dropdown diagnoses-dropdown" style={{ textAlign: 'left' }}>
                  {patient.diagnoses.map((diag, index) => (
                    <div key={index} className="dropdown-item diagnoses-item">
                      <span className="diag-code">{diag.code}</span>
                      <span>{diag.description}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div className="detail-item">
              <span className="detail-label">Diagnosis</span>
              <span className="detail-value">
                {patient.diagnosis}
                <span
                  className="inline-flex ml-2 px-2 py-0.5 text-xs font-bold rounded"
                  style={{
                    backgroundColor: patient.stage.includes('IV')
                      ? 'var(--color-clinical-critical-light)'
                      : patient.stage.includes('III')
                        ? 'var(--color-clinical-progression-light)'
                        : 'var(--color-clinical-treatment-light)',
                    color: patient.stage.includes('IV')
                      ? 'var(--color-clinical-critical-dark)'
                      : patient.stage.includes('III')
                        ? 'var(--color-clinical-progression-dark)'
                        : 'var(--color-clinical-treatment-dark)',
                  }}
                >
                  {patient.stage}
                </span>
              </span>
            </div>
          )}

          {patient.careTeam && patient.careTeam.length > 0 ? (
            <div
              className="detail-item detail-hover"
              onMouseEnter={() => setHoveredItem('careTeam')}
              onMouseLeave={() => setHoveredItem(null)}
            >
              <span className="detail-label">Care Teams</span>
              <span className="detail-value detail-clickable">
                {patient.careTeam[0]?.name} ({patient.careTeam[0]?.role})
                {patient.careTeam.length > 1 && ` +${patient.careTeam.length - 1}`}
              </span>
              {hoveredItem === 'careTeam' && patient.careTeam.length > 1 && (
                <div className="detail-dropdown" style={{ textAlign: 'left' }}>
                  {patient.careTeam.map((member, index) => (
                    <div key={index} className="dropdown-item">
                      {member.name} ({member.role})
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div className="detail-item">
              <span className="detail-label">Care Teams</span>
              <span className="detail-value">No care team</span>
            </div>
          )}
        </div>

        {/* Row 2: Wt/Ht/BSA | Next visit | Preferred Phone | Preferred Language */}
        <div className="detail-row">
          {patient.weightHeight && (
            <div className="detail-item">
              <span className="detail-label">Wt/Ht/BSA</span>
              <div
                className="detail-value-wrapper"
                onMouseEnter={() => setHoveredItem('weightHeight')}
                onMouseLeave={() => setHoveredItem(null)}
              >
                <span className="detail-value detail-clickable">{patient.weightHeight}</span>
                {hoveredItem === 'weightHeight' && patient.weightHeightDetails && (
                  <div className="detail-dropdown" style={{ textAlign: 'left' }}>
                    <div className="dropdown-item">
                      <strong>Date of results</strong>
                      <br />
                      {patient.weightHeightDetails.dateRecorded}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {patient.nextVisit && (
            <div className="detail-item">
              <span className="detail-label">Next visit</span>
              <div
                className="detail-value-wrapper"
                onMouseEnter={() => setHoveredItem('nextVisit')}
                onMouseLeave={() => setHoveredItem(null)}
              >
                <span className="detail-value detail-clickable">{formatDisplayDate(patient.nextVisit)}</span>
                {hoveredItem === 'nextVisit' && patient.nextVisitDetails && patient.nextVisitDetails.length > 0 && (
                  <div className="detail-dropdown" style={{ textAlign: 'left' }}>
                    {patient.nextVisitDetails.map((detail, index) => (
                      <div key={index} className="dropdown-item">
                        {index === 0 && <strong>{detail.location}</strong>}
                        {index === 0 && <br />}
                        {detail.type && detail.provider && (
                          <>
                            {detail.type} with {detail.provider}
                            {detail.time && `, ${detail.time}`}
                          </>
                        )}
                        {detail.type && !detail.provider && (
                          <>
                            {detail.type}
                            {detail.time && `, ${detail.time}`}
                          </>
                        )}
                      </div>
                    ))}
                    {patient.nextVisitDetails.some((d) => d.hasUnscheduledOrders) && (
                      <div className="dropdown-item unscheduled-orders">
                        <span style={{ color: '#D97706' }}>⚠</span> Location has unscheduled orders
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {patient.preferredPhone && (
            <div className="detail-item">
              <span className="detail-label">Preferred Phone</span>
              <span className="detail-value">{patient.preferredPhone}</span>
            </div>
          )}

          {patient.preferredLanguage && (
            <div className="detail-item detail-item-preferred-language">
              <span className="detail-label">Preferred Language</span>
              <span className="detail-value">{patient.preferredLanguage}</span>
            </div>
          )}
        </div>

        {/* Row 3: Pronouns | Next MD visit | Primary Insurance | Preferred Location */}
        <div className="detail-row">
          {patient.pronouns && (
            <div className="detail-item">
              <span className="detail-label">Pronouns</span>
              <span className="detail-value">{patient.pronouns}</span>
            </div>
          )}

          {patient.nextAppointment && (
            <div className="detail-item">
              <span className="detail-label">Next MD visit</span>
              <div
                className="detail-value-wrapper"
                onMouseEnter={() => setHoveredItem('nextMDVisit')}
                onMouseLeave={() => setHoveredItem(null)}
              >
                <span className="detail-value detail-clickable">{formatDisplayDate(patient.nextAppointment)}</span>
                {hoveredItem === 'nextMDVisit' && patient.nextMDVisitDetails && (
                  <div className="detail-dropdown" style={{ textAlign: 'left' }}>
                    <div className="dropdown-item">
                      <strong>{patient.nextMDVisitDetails.location}</strong>
                      <br />
                      {patient.nextMDVisitDetails.type && patient.nextMDVisitDetails.provider && (
                        <>
                          {patient.nextMDVisitDetails.type} with {patient.nextMDVisitDetails.provider}
                          {patient.nextMDVisitDetails.time && `, ${patient.nextMDVisitDetails.time}`}
                        </>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {(patient.insuranceCategory || patient.insurance) && (
            <div className="detail-item">
              <span className="detail-label">Primary Insurance</span>
              <span className="detail-value">
                {/* Line of business in bold, payer name in brackets. */}
                <strong>{patient.insuranceCategory || patient.insurance}</strong>
                {patient.insuranceCategory && patient.insurance && patient.insurance !== patient.insuranceCategory
                  ? ` (${patient.insurance})`
                  : null}
                {patient.copay && <span className="copay-text"> • Copay: {patient.copay}</span>}
              </span>
            </div>
          )}

          <div className="detail-item detail-item-preferred-location">
            <span className="detail-label">Preferred Location</span>
            <span className="detail-value">
              {patient.preferredLocation || patient.location || 'Not specified'}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
